export const SITE_URL = 'https://elyspr.com';
export const SITE_NAME = 'Elyspr';
export const DEFAULT_OG_IMAGE = `${SITE_URL}/favicon.avif`;
export const CONTACT_EMAIL = 'info@elyspr.com';
export const CONTACT_PHONE = '+91-8655810187';

export const INDEXABLE_PATHS = [
  '/',
  '/elyconcierge',
  '/elyfleet',
  '/elydine',
  '/elyventory',
  '/elycustomized',
  '/contact'
];

export const SEO_PAGES = {
  home: {
    path: '/',
    title: 'Elyspr | Custom Website Development & Software Solutions Agency',
    description:
      'Elyspr builds high-converting business websites and custom software — web apps, mobile apps, APIs, and automation. Get a tailored digital solution for your business. Book a free consultation.',
    keywords:
      'custom website development, business website design, custom software development company, web application development, software development agency India, bespoke software solutions, digital transformation agency',
    ogImage: DEFAULT_OG_IMAGE,
    priority: 1.0
  },
  elychat: {
    path: '/elyconcierge',
    title: 'ElyConcierge | WhatsApp Guest Communication for Hotels & Resorts | Elyspr',
    description:
      'ElyConcierge automates hotel guest communication on WhatsApp — requests, concierge, room service, and operations. Built for hotels, resorts, and hospitality teams.',
    keywords:
      'hotel WhatsApp software, guest communication platform, hospitality messaging automation, WhatsApp hotel concierge, resort guest services software',
    ogImage: `${SITE_URL}/ElyChat.avif`,
    priority: 0.8
  },
  elyfleet: {
    path: '/elyfleet',
    title: 'ElyFleet | Real-Time Resort Fleet Management & GPS Tracking | Elyspr',
    description:
      'Know where every car is. Every minute. ElyFleet provides real-time vehicle GPS tracking, geofence monitoring, idle engine detection, and trip dispatch for luxury resorts and hospitality properties.',
    keywords:
      'resort fleet management, hotel vehicle tracking software, real-time GPS fleet tracking, resort golf cart tracking, property vehicle dispatch system, geofence fleet management, idle engine monitoring',
    ogImage: `${SITE_URL}/laptopelyfleetclear.webp`,
    priority: 0.85
  },
  elydine: {
    path: '/elydine',
    title: 'ElyDine | Restaurant Management & QR Ordering Software | Elyspr',
    description:
      'ElyDine is offline-ready restaurant software with QR ordering, kitchen displays, billing, and inventory — built for modern restaurants and food businesses.',
    keywords:
      'restaurant management software, QR menu ordering system, restaurant POS software, kitchen display system, offline restaurant billing software',
    ogImage: `${SITE_URL}/ElyDine.avif`,
    priority: 0.8
  },
  elyventory: {
    path: '/elyventory',
    title: 'ElyVentory | Hotel & Resort Inventory Management Software | Elyspr',
    description:
      'ElyVentory tracks stock across properties with barcode scanning, automated reordering, multi-location dashboards, and real-time hospitality inventory analytics.',
    keywords:
      'hotel inventory management software, resort stock tracking, barcode inventory system, hospitality warehouse management, property inventory software',
    ogImage: DEFAULT_OG_IMAGE,
    priority: 0.8
  },
  elycustomized: {
    path: '/elycustomized',
    title: 'Custom Software Development Company | Bespoke Web & Mobile Apps | Elyspr',
    description:
      'Hire Elyspr for custom software development — MVPs, enterprise systems, APIs, integrations, and automation built around your workflow. From discovery to deployment.',
    keywords:
      'custom software development company, bespoke software development, hire custom software developers, enterprise software development, custom web application development, API integration services, business automation software',
    ogImage: DEFAULT_OG_IMAGE,
    priority: 0.95
  },
  contact: {
    path: '/contact',
    title: 'Contact Elyspr | Get a Quote for Website & Custom Software Development',
    description:
      'Contact Elyspr to discuss your website or custom software project. Book a call with our team — info@elyspr.com | 8655810187. Free consultation for businesses.',
    keywords:
      'contact software development company, website development quote, custom software consultation, hire web developers, Elyspr contact',
    ogImage: DEFAULT_OG_IMAGE,
    priority: 0.9
  }
};

export const buildCanonical = (path) => `${SITE_URL}${path === '/' ? '' : path}`;

export const buildOrganizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': `${SITE_URL}/#organization`,
  name: SITE_NAME,
  url: SITE_URL,
  logo: DEFAULT_OG_IMAGE,
  email: CONTACT_EMAIL,
  telephone: CONTACT_PHONE,
  description:
    'Elyspr is a business digitalization agency specializing in custom website development, bespoke software, and hospitality SaaS products.',
  sameAs: [
    'https://www.instagram.com/elyspr.in',
    'https://www.linkedin.com/company/elyspr'
  ],
  contactPoint: [
    {
      '@type': 'ContactPoint',
      contactType: 'sales',
      email: CONTACT_EMAIL,
      telephone: CONTACT_PHONE,
      areaServed: 'IN',
      availableLanguage: ['English', 'Hindi']
    }
  ]
});

export const buildWebSiteSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${SITE_URL}/#website`,
  name: SITE_NAME,
  url: SITE_URL,
  description: SEO_PAGES.home.description,
  publisher: { '@id': `${SITE_URL}/#organization` },
  potentialAction: {
    '@type': 'SearchAction',
    target: `${SITE_URL}/contact?q={search_term_string}`,
    'query-input': 'required name=search_term_string'
  }
});

export const buildWebPageSchema = (pageKey) => {
  const page = SEO_PAGES[pageKey];
  if (!page) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${buildCanonical(page.path)}#webpage`,
    url: buildCanonical(page.path),
    name: page.title,
    description: page.description,
    isPartOf: { '@id': `${SITE_URL}/#website` },
    about: { '@id': `${SITE_URL}/#organization` },
    inLanguage: 'en-IN'
  };
};

export const buildServiceSchema = ({ name, description, path, serviceType = 'SoftwareDevelopment' }) => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  name,
  description,
  url: buildCanonical(path),
  provider: { '@id': `${SITE_URL}/#organization` },
  areaServed: {
    '@type': 'Country',
    name: 'India'
  },
  serviceType,
  offers: {
    '@type': 'Offer',
    url: buildCanonical('/contact'),
    availability: 'https://schema.org/InStock',
    price: '0',
    priceCurrency: 'INR',
    description: 'Free consultation and project quote'
  }
});

export const buildBreadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: buildCanonical(item.path)
  }))
});

export const buildFaqSchema = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer
    }
  }))
});

export const buildContactPageSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  '@id': `${buildCanonical('/contact')}#contactpage`,
  url: buildCanonical('/contact'),
  name: SEO_PAGES.contact.title,
  description: SEO_PAGES.contact.description,
  mainEntity: {
    '@type': 'Organization',
    '@id': `${SITE_URL}/#organization`
  }
});

export const HOME_SERVICES_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'Elyspr Services',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      item: {
        '@type': 'Service',
        name: 'Custom Website Development',
        url: buildCanonical('/contact'),
        description: 'Professional business websites designed for performance, branding, and lead generation.'
      }
    },
    {
      '@type': 'ListItem',
      position: 2,
      item: {
        '@type': 'Service',
        name: 'Custom Software Development',
        url: buildCanonical('/elycustomized'),
        description: 'Bespoke web apps, mobile apps, APIs, and enterprise software built to your requirements.'
      }
    },
    {
      '@type': 'ListItem',
      position: 3,
      item: {
        '@type': 'Service',
        name: 'ElyConcierge Hospitality Software',
        url: buildCanonical('/elyconcierge')
      }
    },
    {
      '@type': 'ListItem',
      position: 4,
      item: {
        '@type': 'Service',
        name: 'ElyDine Restaurant Software',
        url: buildCanonical('/elydine')
      }
    },
    {
      '@type': 'ListItem',
      position: 5,
      item: {
        '@type': 'Service',
        name: 'ElyVentory Inventory Software',
        url: buildCanonical('/elyventory')
      }
    }
  ]
};

export const buildProductPageSchemas = (pageKey, faqs, serviceName, serviceDescription) => {
  const page = SEO_PAGES[pageKey];
  if (!page) return [];

  return [
    buildBreadcrumbSchema([
      { name: 'Home', path: '/' },
      { name: serviceName, path: page.path }
    ]),
    buildServiceSchema({
      name: serviceName,
      description: serviceDescription,
      path: page.path
    }),
    buildFaqSchema(faqs)
  ];
};
